import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import type { TelemetryConsentStore } from '@/application/telemetry/consentStore'
import { TelemetryConsentContext } from './telemetryConsentContext'

/**
 * Reactivity layer over the {@link TelemetryConsentStore}. The store
 * holds the synchronous cached value used by the
 * {@code ConsentingTelemetryAdapter}; the provider re-renders any
 * UI that consumes the value when the user toggles it.
 *
 * `useSyncExternalStore` is the canonical React hook for external
 * stores — tear-free under concurrent rendering. The store's
 * `subscribe` is forwarded; the snapshot returns the cached boolean.
 *
 * Calls `store.load()` on mount so the toggle UI reflects the
 * persisted value rather than the initial `false`.
 */
export function TelemetryConsentProvider({
  store,
  children,
}: {
  store: TelemetryConsentStore
  children: ReactNode
}) {
  const consenting = useSyncExternalStore(
    (onChange) => store.subscribe(onChange),
    () => store.isConsenting(),
    () => false,
  )
  const loaded = useSyncExternalStore(
    (onChange) => store.subscribe(onChange),
    () => store.isLoaded(),
    () => false,
  )
  const dismissed = useSyncExternalStore(
    (onChange) => store.subscribe(onChange),
    () => store.wasDismissed(),
    () => false,
  )

  useEffect(() => {
    if (!store.isLoaded()) {
      void store.load()
    }
  }, [store])

  const value = useMemo(
    () => ({
      consenting,
      loaded,
      dismissed,
      setConsent: (next: boolean) => store.set(next),
      dismissPrompt: () => store.dismiss(),
    }),
    [store, consenting, loaded, dismissed],
  )

  return (
    <TelemetryConsentContext.Provider value={value}>
      {children}
    </TelemetryConsentContext.Provider>
  )
}
